import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useTheme, listThemes } from '@evi/skins';
import type { ModeId, Theme } from '@evi/skins';
import { ChargingAnimation, driverThemeName } from '@evi/ui';
import { ExitOverlayContext } from '@/components/AppShell';
import { fetchUnlocks, type UnlockCard, type ThemeUnlockKind } from '@/lib/api';
import { useApplyTheme } from '@/hooks/useApplyTheme';
import { HowToEarnSheet } from './HowToEarnSheet';
import styles from './ThemesPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * Full-screen themes browser at /themes. Lists every available theme —
 * admin families (EVI Default, Luce) and skin unlocks (Midnight, Sunset,
 * Neon, Obsidian…) — as equal-weight cards in registry order.
 *
 * Each card shows a preview area, the name + cost/description, both
 * variant swatches, and an action row (Apply / Active / Unlock-at-N).
 * Preview is a theme-tinted gradient placeholder in Step 5;
 * `<ChargingAnimation>` slots in at the marked TODO in Step 6.
 *
 * Entry paths:
 *   - /themes                       → normal browse, no scroll
 *   - /themes?theme=<code>          → auto-scroll to that card + brief
 *                                     focus pulse. Used by the picker's
 *                                     locked-row tap.
 */
export function ThemesPage({ onNavigate }: Props): JSX.Element {
  const { t } = useTranslation('themes');
  const { mode } = useTheme();
  const { applyTheme } = useApplyTheme();

  const unlocksQ = useQuery({
    queryKey: ['unlocks'],
    queryFn: fetchUnlocks,
    // A fresh fetch on every entry is worth the round-trip — the page
    // is the authoritative "what do I own" surface, and a stale owned
    // flag here would mean the driver couldn't apply a just-purchased
    // skin without a manual reload.
    staleTime: 0,
  });

  const cards = useMemo<ThemeCard[]>(
    () => buildThemeCards({
      themes: listThemes(),
      unlocks: unlocksQ.data?.unlocks ?? [],
      activeSkinCode: unlocksQ.data?.activeSkinCode ?? null,
      // User totals — top-level fields on the /me/unlocks response.
      // Threaded through so per-card progress copy can render
      // "current / target" without a second fetch.
      userTotalKwh: unlocksQ.data?.userTotalKwh ?? 0,
      userTotalSessions: unlocksQ.data?.userTotalSessions ?? 0,
    }),
    [unlocksQ.data],
  );

  // Focus-on-mount + scroll-to-anchor for ?theme=<code>. The picker's
  // locked-row tap routes here with the code so the driver lands right
  // on what they wanted.
  const [focusCode, setFocusCode] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('theme');
    if (!code) return;
    setFocusCode(code);
  }, []);
  useEffect(() => {
    if (!focusCode) return;
    if (cards.length === 0) return; // wait for cards to render
    const el = gridRef.current?.querySelector<HTMLElement>(
      `[data-theme-code="${CSS.escape(focusCode)}"]`,
    );
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Clear the focus class after the pulse animation runs (2s).
    const t = window.setTimeout(() => setFocusCode(null), 2200);
    return () => window.clearTimeout(t);
  }, [focusCode, cards.length]);

  // Locked-tap → modal with "how to earn" breakdown.
  const [lockedModal, setLockedModal] = useState<ThemeCard | null>(null);

  const handleCardTap = (card: ThemeCard): void => {
    if (card.locked) {
      setLockedModal(card);
      return;
    }
    // Apply (or no-op-with-toast if already active — handled by the hook).
    void applyTheme(card);
  };

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => onNavigate('/profile')}>
        <ArrowLeft size={14} /> {t('back')}
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>{t('title')}</h1>
        <p className={styles.subtitle}>
          {t('subtitle')}
        </p>
      </header>

      {unlocksQ.isLoading && cards.length === 0 ? (
        <GridSkeleton />
      ) : (
        <div ref={gridRef} className={styles.grid}>
          {cards.map((card) => (
            <ThemeCardView
              key={card.code}
              userTotalKwh={unlocksQ.data?.userTotalKwh ?? 0}
              userTotalSessions={unlocksQ.data?.userTotalSessions ?? 0}
              card={card}
              mode={mode}
              focused={card.code === focusCode}
              onTap={() => handleCardTap(card)}
            />
          ))}
        </div>
      )}

      {lockedModal && (
        <HowToEarnSheet
          themeName={lockedModal.name}
          themeCost={lockedModal.pricePoints ?? 0}
          currentPoints={unlocksQ.data?.totalPoints ?? 0}
          // Milestone fields — null/undefined for legacy points-style
          // locks; HowToEarnSheet branches on unlockKind to render
          // either the points-trade flow (legacy) or the kWh-/sessions-
          // milestone progress copy (post unlock-model rewrite).
          unlockKind={lockedModal.unlockCriteriaKind ?? null}
          unlockThreshold={lockedModal.unlockCriteriaThreshold ?? null}
          currentKwh={unlocksQ.data?.userTotalKwh ?? 0}
          currentSessions={unlocksQ.data?.userTotalSessions ?? 0}
          onClose={() => setLockedModal(null)}
          onNavigateRewards={() => {
            setLockedModal(null);
            onNavigate('/rewards');
          }}
        />
      )}
    </div>
  );
}

// ── Card view ────────────────────────────────────────────────────────────

function ThemeCardView({
  card,
  mode,
  focused,
  onTap,
  userTotalKwh,
  userTotalSessions,
}: {
  card: ThemeCard;
  mode: ModeId;
  focused: boolean;
  onTap: () => void;
  // User totals shared across every card on the same page render.
  // Threaded as props rather than baked into ThemeCard so the card
  // shape stays minimal — the totals are a single value, not a
  // per-card derivation.
  userTotalKwh: number;
  userTotalSessions: number;
}): JSX.Element {
  const { t } = useTranslation('themes');
  const variant = card.variants[mode];
  // Inside the exit-overlay clone (menu→fullscreen transition,
  // AppShell pageExitDown) this page is a SECOND live mount whose
  // effects run. A full grid of live WebGL sims here re-acquires 6-9
  // GL contexts for the 280ms slide — against a browser cap of ~8 on
  // mobile Safari — exactly while the destination map is constructing
  // and uploading tile textures. That eviction is the themes→map
  // first-paint bug. The clone renders static posters instead (same
  // opt-out pattern as ChargingPage / PreSessionPage).
  const inExitOverlay = useContext(ExitOverlayContext);
  return (
    <article
      data-theme-code={card.code}
      className={`${styles.card} ${focused ? styles.cardFocused : ''} ${
        card.active ? styles.cardActive : ''
      } ${card.locked ? styles.cardLocked : ''}`}
    >
      {/*
        Per-theme charging animation. Uses inline accent/charging
        overrides so each card paints in its own colours without
        flipping the global skin override — the driver can see what
        Midnight looks like without wearing it.

        Driver Themes ticket — Commit 9 adjustment. Before this
        commit, every card passed `active={false}` unconditionally.
        That was harmless when every card routed to a CSS-keyframe
        animation (keyframes ignore the `active` flag), but became
        a regression after Commit 8 wired the 5 shader families
        through ShaderChargingAnimation: that component honors
        `active === false` by short-circuiting the RAF draw call,
        so the currently-applied card stopped animating in the
        picker. Pass `active={card.active}` so the applied card
        renders live and the others stay paused. `glow="subtle"`
        keeps every card's frame glow uniform regardless of
        active state (without this override, `active=true` would
        flip the dispatcher's frame to the breathing-glow class,
        making the applied card visually scream). The `Active`
        badge + border glow on the card wrapper still signal
        which family is applied.
      */}
      <div className={styles.preview}>
        {/*
          Hotfix (post-C11) — drop the fixed `size={140}` so the
          dispatcher's frame doesn't pin the inner element at a
          140px square inside the responsive .preview container
          (which is aspect-ratio: 1 / 1 + flex-centered, so it
          scales with the grid column from ~123px on small mobile
          up to ~301px on desktop). The dispatcher defaults size
          to 220 when the prop is omitted; `.preview > div` +
          `.preview canvas` overrides in the stylesheet stretch
          the frame and the shader canvas to fill the container.
          CSS-module animations (Luce, Midnight, Sunset, Bloom)
          still render at their own internal sizing centered in
          the now-larger frame — acceptable visual trade-off for
          the hotfix.
        */}
        {/*
          Follow-up hotfix — `active={true}` on every card (was
          `card.active`). The previous hotfix's first-frame guard
          (ShaderChargingAnimation.hasRenderedOnceRef) made
          non-active cards paint a single static frame, which was
          better than blank but still left 6 of 7 cards visually
          frozen while only the currently-applied card kept moving.
          Picker wants ALL cards animated for visual richness so
          drivers can see each theme's living motion before
          committing. The "currently-applied" signal flows
          exclusively through the separate `Active` badge + the
          card border glow keyed off `card.active` — the animation's
          own `active` prop is decoupled from that semantic. The
          `glow="subtle"` override below holds the dispatcher's
          frame glow flat regardless of `active`, so the breathing
          pulse doesn't fire on every card and steal attention.

          Perf trade-off acknowledged: 5 simultaneous shader cards
          (initiative, electric-light, dark-electric, neon,
          obsidian) animating instead of just the active one. Per
          Warren's product call. If mobile jank surfaces in prod,
          follow-up options are captured at the bottom of the
          C11 commit body (IntersectionObserver staggered mount,
          framerate throttling, prewarm pass).

          `percent` bumped to 80 (was 60) to match Warren's
          intended preview state — affects how filled the
          charge-progress UI reads on CSS animations and any
          shader uniform that maps from percent (orb fill,
          sun-position trajectory bounds).
        */}
        {inExitOverlay ? (
          /* Poster, stated plainly: a static gradient in the card's
             own accent/charging colours — NOT a captured frame (the
             clone owns no canvas; the originals render without
             preserveDrawingBuffer, so their pixels can't be read
             back). The clone only exists for the 280ms slide-down,
             whose motion carries the transition. */
          <div
            className={styles.previewPoster}
            style={{
              '--poster-accent': variant.accent,
              '--poster-charging': variant.charging,
            } as CSSProperties}
          />
        ) : (
          <ChargingAnimation
            themeCode={card.code}
            mode={mode}
            percent={80}
            active={true}
            glow="subtle"
            accentOverride={variant.accent}
            chargingOverride={variant.charging}
          />
        )}
      </div>

      <div className={styles.body}>
        <div className={styles.nameRow}>
          <span className={styles.name}>{card.name}</span>
          {card.active && <span className={styles.activeBadge}>{t('active')}</span>}
        </div>
        <p className={styles.desc}>
          {/*
            Legacy points-style locks show "N pts" here; milestone
            locks show their description instead (the kWh/sessions
            target lives in the CTA + progress hint below, so
            repeating a meaningless "0 pts" in the desc slot would
            mislead). Milestone skins carry pricePoints: 0, which the
            old `pricePoints != null` check rendered as "0 pts" \u2014
            guard on unlockCriteriaKind so only true points-locks
            take the pts branch.
          */}
          {card.locked && card.pricePoints != null
            && card.unlockCriteriaKind !== 'kwh_milestone'
            && card.unlockCriteriaKind !== 'session_milestone'
            ? t('ptsValue', { value: card.pricePoints.toLocaleString() })
            : card.description || '\u00A0'}
        </p>

        <div className={styles.swatchBlock} aria-hidden>
          <SwatchRow label={t('swatch.light')} variant={card.variants.light} />
          <SwatchRow label={t('swatch.dark')}  variant={card.variants.dark} />
        </div>

        <button
          type="button"
          className={
            card.locked
              ? styles.lockedAction
              : card.active
                ? styles.activeAction
                : styles.applyAction
          }
          onClick={onTap}
        >
          {!card.locked && (card.active ? t('active') : t('apply'))}
          {card.locked && lockedCtaCopy(card, t)}
        </button>
        {/*
          Progress hint — only renders for milestone-kind unlocks
          that are still locked. Compact "current / target" line in
          muted text below the CTA. No progress bar, no animations
          (per Commit 4 build-prompt spec). The CTA above already
          carries the absolute target; this line carries the
          relative progress so the driver sees how close they are
          at a glance without tapping into the modal.

          Renders nothing for legacy points-style locks (no kind set
          or kind === 'points'-equivalent), unowned-via-tier-gate
          locks (admin_only / fleet_only never reach the picker for
          non-super-admin), or already-unlocked cards.
        */}
        {card.locked && card.unlockCriteriaKind === 'kwh_milestone'
          && card.unlockCriteriaThreshold != null && (
          <div className={styles.unlockProgress}>
            {Math.floor(userTotalKwh).toLocaleString()} / {card.unlockCriteriaThreshold.toLocaleString()} kWh
          </div>
        )}
        {card.locked && card.unlockCriteriaKind === 'session_milestone'
          && card.unlockCriteriaThreshold != null && (
          <div className={styles.unlockProgress}>
            {t('progressSessions', {
              current: userTotalSessions.toLocaleString(),
              target: card.unlockCriteriaThreshold.toLocaleString(),
            })}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * Locked-CTA copy resolver. Branches on `unlockCriteriaKind` so each
 * unlock kind gets its native unit ("kWh" / "sessions"). Falls through
 * to the legacy points copy for any kind without explicit handling —
 * means a future Unlock enum addition that's not wired into this UI
 * still gracefully renders "Unlock at N pts" rather than blanking.
 *
 * Ordering matters: explicit-kind branches first, fallback last.
 */
function lockedCtaCopy(
  card: ThemeCard,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (card.unlockCriteriaKind === 'kwh_milestone' && card.unlockCriteriaThreshold != null) {
    return t('cta.unlockKwh', { value: card.unlockCriteriaThreshold.toLocaleString() });
  }
  if (card.unlockCriteriaKind === 'session_milestone' && card.unlockCriteriaThreshold != null) {
    return t('cta.unlockSessions', { value: card.unlockCriteriaThreshold.toLocaleString() });
  }
  return t('cta.unlockPts', { value: (card.pricePoints ?? 0).toLocaleString() });
}

function SwatchRow({
  label,
  variant,
}: {
  label: string;
  variant: ThemeCard['variants'][ModeId];
}): JSX.Element {
  return (
    <div className={styles.swatchRow}>
      <span className={styles.swatchLabel}>{label}</span>
      <span className={styles.swatches}>
        <span className={styles.swatch} style={{ background: variant.accent }} />
        <span className={styles.swatch} style={{ background: variant.charging }} />
        {variant.surface && (
          <span className={styles.swatch} style={{ background: variant.surface }} />
        )}
      </span>
    </div>
  );
}

function GridSkeleton(): JSX.Element {
  return (
    <div className={styles.grid}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={`${styles.card} ${styles.cardSkeleton}`} />
      ))}
    </div>
  );
}

// ── Card shape + builder ─────────────────────────────────────────────────

export interface ThemeCard {
  code: string;
  name: string;
  description: string;
  kind: 'family' | 'skin';
  owned: boolean;
  active: boolean;
  locked: boolean;
  pricePoints?: number;
  variants: {
    light: { accent: string; charging: string; surface?: string };
    dark:  { accent: string; charging: string; surface?: string };
  };
  // Theme-milestone fields (post unlock-model rewrite Commit 4).
  // Mirror the UnlockCard shape from @/lib/api so the picker can
  // render kWh-milestone progress copy instead of the legacy
  // "Unlock at N pts" string. `unlockCriteriaKind` carries the
  // full Unlock.unlock_criteria enum union (default / kwh_milestone
  // / session_milestone / fleet_only / admin_only), but the UI
  // only branches on kwh_milestone + session_milestone — everything
  // else falls through to the pricePoints legacy copy.
  unlockCriteriaKind?: ThemeUnlockKind | null;
  unlockCriteriaThreshold?: number | null;
  /** 0-100, computed server-side; null when criteria isn't a milestone kind. */
  progressPercent?: number | null;
}

interface BuildInput {
  themes: Theme[];
  unlocks: UnlockCard[];
  activeSkinCode: string | null;
  // User's current totals — sourced from the /me/unlocks response's
  // top-level fields (NOT per-card). Threaded through so per-card
  // progress copy can show "current / target" without a second
  // fetch. Optional defaults (0) cover the loading-state path.
  userTotalKwh?: number;
  userTotalSessions?: number;
}

/**
 * Flatten the registry + unlocks catalogue into a single cards list.
 * Families first (EVI Default, Luce), then skin unlocks in catalogue
 * order. Non-skin unlocks (priority_booking, lounge_access) are
 * filtered out — this page is themes-only.
 */
function buildThemeCards(input: BuildInput): ThemeCard[] {
  const out: ThemeCard[] = [];

  // Admin families — owned/locked is provisional here. Most families
  // ship `unlockedByDefault: true` and stay owned+unlocked, but a
  // milestone-gated family (Aurora, Cyber) is dual-registered: it
  // exists in the family registry (for shader rendering) AND carries
  // a matching skin Unlock doc (for the kWh gate). The gating fields
  // get grafted from that skin doc in the merge pass below — so we
  // seed `owned`/`locked` from `unlockedByDefault` rather than a flat
  // `true`/`false`. A dual-registered family's seed is overwritten by
  // the graft; a non-gated family keeps the default-true seed.
  // Display name runs through `driverThemeName` for re-branding on
  // driver surfaces without disturbing the shared registry.
  const familyByThemeId = new Map<string, ThemeCard>();
  for (const t of input.themes) {
    const unlockedByDefault = t.unlockedByDefault !== false;
    const card: ThemeCard = {
      code: t.id,
      name: driverThemeName(t.id, t.name),
      description: t.description,
      kind: 'family',
      owned: unlockedByDefault,
      locked: !unlockedByDefault,
      active: !input.activeSkinCode && isCurrentFamily(t.id, input),
      variants: {
        light: {
          accent: t.variants.light.tokens.colors.accent,
          charging: t.variants.light.tokens.colors.charging,
          surface: t.variants.light.tokens.colors.surface,
        },
        dark: {
          accent: t.variants.dark.tokens.colors.accent,
          charging: t.variants.dark.tokens.colors.charging,
          surface: t.variants.dark.tokens.colors.surface,
        },
      },
    };
    out.push(card);
    familyByThemeId.set(t.id, card);
  }

  // Skin unlocks — pull from unlocks catalogue. Owned / tier flags
  // already reflect canBypassUnlocks bypass (Step 3), so superadmin
  // sees every skin as `owned: true`.
  //
  // Dedup merge: when a skin's `metadata.themeId` matches a family
  // card's `id` AND THAT FAMILY IS MILESTONE-GATED, the two are the
  // same theme registered twice (family = shader rendering, skin =
  // kWh gate — the Aurora/Cyber pattern). Graft the skin's gating
  // state onto the family card and DROP the skin card.
  //
  // Critical guard: graft only fires when familyCard.locked === true
  // (i.e. the family's unlockedByDefault is false — Aurora/Cyber
  // today). Skins composing OVER an unlocked-by-default family
  // (Sunset/Bloom/Midnight composing over Default via the legitimate
  // metadata.themeId:'default' seed convention) must NOT graft —
  // doing so would overwrite Default's "Apply" state with the skin's
  // "Unlock at N kWh" state, breaking INITIATIVE for everyone.
  //
  // Without this guard a Bloom skin (locked at 550 kWh) matches the
  // Default family card (id='default'), grafts its locked state on,
  // and INITIATIVE shows "Unlock at 550 kWh" instead of "Apply".
  // That's the bug a Warren live walk caught post-f967c6c. The seed
  // is correct (skin_bloom legitimately composes over Default per
  // packages/db/src/seeds/unlocks.ts:9); the dedup was too eager.
  for (const u of input.unlocks) {
    if (u.category !== 'skin') continue;
    const meta = u.metadata as Record<string, unknown>;
    const themeId = meta['themeId'] as string | undefined;
    const locked = !u.owned || !u.tierEligible;

    const familyCard = themeId ? familyByThemeId.get(themeId) : undefined;
    if (familyCard && familyCard.locked) {
      // Graft the skin's gating state onto the family card. The family
      // keeps its kind/code/name/variants (shader rendering + family
      // apply path); only the lock-state + milestone copy fields come
      // from the skin doc. The skin card itself is not pushed.
      familyCard.owned = u.owned;
      familyCard.locked = locked;
      familyCard.active = familyCard.active || u.active;
      familyCard.pricePoints = u.pricePoints;
      familyCard.unlockCriteriaKind = u.unlockCriteriaKind;
      familyCard.unlockCriteriaThreshold = u.unlockCriteriaThreshold;
      familyCard.progressPercent = u.progressPercent;
      continue;
    }

    const metaVariants = meta['variants'] as
      | { light?: Record<string, unknown>; dark?: Record<string, unknown> }
      | undefined;
    const lightV = variantBlock(metaVariants?.light, meta);
    const darkV  = variantBlock(metaVariants?.dark,  meta);

    out.push({
      code: u.code,
      name: u.name,
      description: u.description,
      kind: 'skin',
      owned: u.owned,
      locked,
      active: u.active,
      pricePoints: u.pricePoints,
      variants: { light: lightV, dark: darkV },
      // Pass through milestone fields — pure mapping, no derivation.
      // The CTA branch + progress hint in ThemeCardView consumes
      // these to decide between kwh-milestone copy and the legacy
      // points-fallback copy.
      unlockCriteriaKind: u.unlockCriteriaKind,
      unlockCriteriaThreshold: u.unlockCriteriaThreshold,
      progressPercent: u.progressPercent,
    });
  }

  return out;
}

/**
 * Coerce the metadata variant block — or the legacy flat
 * accentColor/chargingColor fields — into the renderable shape the
 * card uses. Mirrors the tolerance in useSkinSync: a pre-migration
 * doc still renders its single colour in both card swatches instead
 * of showing grey placeholders.
 */
function variantBlock(
  v: Record<string, unknown> | undefined,
  metaRoot: Record<string, unknown>,
): { accent: string; charging: string; surface?: string } {
  const accent =
    (v?.['accentColor'] as string | undefined) ??
    (metaRoot['accentColor'] as string | undefined) ??
    '#888888';
  const charging =
    (v?.['chargingColor'] as string | undefined) ??
    (metaRoot['chargingColor'] as string | undefined) ??
    accent;
  const surface = v?.['surface'] as string | undefined;
  return surface ? { accent, charging, surface } : { accent, charging };
}

/**
 * Family is "currently active" when its id matches the user's current
 * family preference AND no skin override is painting. We can't read
 * the family directly from the unlocks payload (skins scope only),
 * so callers compose this via useTheme upstream. The ThemesPage
 * passes `activeSkinCode` into the builder and delegates the
 * family match to its own useTheme read.
 *
 * In Step 5 we approximate by trusting the single active family from
 * the registry's perspective — if a skin is active, no family shows
 * active regardless. useTheme is not in scope here (builder is a pure
 * function), so the ThemesPage passes `activeSkinCode` and the
 * default family-id match resolves against `document` mode via the
 * CSS var on the root — intentionally ambient rather than authoritative.
 */
function isCurrentFamily(id: string, input: BuildInput): boolean {
  if (input.activeSkinCode) return false;
  // Read the persisted family from localStorage so the builder stays
  // a pure function (no hook access). This matches what ThemeProvider
  // itself reads on boot, so it's authoritative enough.
  if (typeof window === 'undefined') return id === 'default';
  const stored = window.localStorage.getItem('evi.theme');
  return (stored ?? 'default') === id;
}
